/**
 * DeskQueueView — the Desk's default view, and the redesign's home screen
 * (FEAT-087 / S-365).
 *
 * One prioritised list of everything stopped and waiting on a person,
 * grouped by the kind of work rather than by pipeline phase. The grouping
 * itself lives in `@/lib/queue-groups` so it can be tested without React;
 * this file is the rendering, the header, and the actions.
 *
 * Every number here is derived from the rows on screen — the headline
 * count, each group's count, the "still working" tally. The handoff is
 * explicit that none of them are ever typed, because that is what keeps
 * the screen honest when the data changes.
 *
 * Values are the design file's own, read from
 * `design_handoff_nagareru_redesign/nagareru - Redesign.dc.html` rather
 * than from the README's prose summary, per the handoff's instruction that
 * the comp wins where the two disagree.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, useApiQuery } from "@/lib/api";
import { LoadingState } from "@/components/LoadingState";
import { Icon } from "@/components/Icon";
import { useWorkspaceContext } from "@/lib/workspace-context";
import { useDateFormat } from "@/lib/useDateFormat";
import { formatDate, formatDateTime } from "@/lib/date-format";
import { formatContentType } from "@/lib/format";
import {
  groupQueue,
  isOverdue,
  isPipelineRunning,
  queueReasonFor,
  stoppedByAgent,
  type QueueGroup,
} from "@/lib/queue-groups";
import { useDeskContext } from "./DeskView";
import { useRole } from "@/lib/useRole";

interface PieceRow {
  id: number;
  title: string;
  slug: string;
  content_type: string;
  ui_state: string;
  generation_phases: string | null;
  publication_date: string | null;
  updated_at: string;
  target_word_count: number | null;
}

interface PiecesListResponse {
  data: PieceRow[];
  meta: { total: number; limit: number; offset: number };
}

export function DeskQueueView() {
  const { openCreatePiece } = useDeskContext();
  const { canEdit } = useRole();
  const navigate = useNavigate();
  const dateFormat = useDateFormat();
  const { activeWorkspace, allWorkspaces, isLoading: wsLoading } = useWorkspaceContext();
  const activeId = activeWorkspace?.id;

  const { data, isLoading, error } = useApiQuery<PiecesListResponse>(
    ["pieces", activeId ?? 0, "desk-queue"],
    `/pieces?agencyclient_id=${activeId}`,
    { refetchOnWindowFocus: true, enabled: !!activeId && !allWorkspaces }
  );

  const pieces = useMemo(() => data?.data ?? [], [data]);
  const grouped = useMemo(() => groupQueue(pieces), [pieces]);

  // Derived, never typed — see the module header.
  const waitingCount = useMemo(
    () => grouped.reduce((n, g) => n + g.pieces.length, 0),
    [grouped]
  );
  const overdueCount = useMemo(
    () => pieces.filter((p) => isOverdue(p)).length,
    [pieces]
  );
  const ideaCount = useMemo(
    () => grouped.find((g) => g.group === "start")?.pieces.length ?? 0,
    [grouped]
  );
  const runningCount = useMemo(
    () => pieces.filter(isPipelineRunning).length,
    [pieces]
  );

  if (wsLoading || isLoading) {
    return <LoadingState label="Loading your desk…" />;
  }

  if (error) {
    return (
      <div className="state-error">
        {error instanceof ApiError && error.status === 403
          ? "You don't have permission to view this workspace."
          : (error.message ?? "Could not load your desk.")}
      </div>
    );
  }

  return (
    <div className="desk-queue">
      <header className="desk-head cal-head">
        <div>
          <p className="desk-eyebrow">{longToday(dateFormat)}</p>
          <h1 className="desk-title">{headline(waitingCount)}</h1>
          <p className="desk-sub">{subtitle(overdueCount, ideaCount, runningCount)}</p>
        </div>
        <div className="cal-head-r">
          {canEdit && (
            <button type="button" className="desk-new" onClick={openCreatePiece}>
              + New piece
            </button>
          )}
        </div>
      </header>

      {grouped.length === 0 ? (
        <div className="desk-empty">
          <h2>Nothing is waiting on you</h2>
          <p>
            {runningCount > 0
              ? `${runningCount} ${runningCount === 1 ? "piece is" : "pieces are"} still with the agents. They will appear here when they need you.`
              : "When a piece stops and needs a person, it shows up here."}
          </p>
        </div>
      ) : (
        grouped.map(({ group, meta, pieces: rows }) => (
          <section key={group} className="desk-group" data-kind={group}>
            <div className="desk-group-head">
              <span className="desk-group-label">{meta.label}</span>
              <span className="desk-group-desc">{meta.description}</span>
              <span className="desk-group-spacer" />
              <span className="desk-group-count">{rows.length}</span>
            </div>
            <div className="desk-rows">
              {rows.map((piece) => (
                <QueueRow
                  key={piece.id}
                  piece={piece}
                  group={group}
                  dateFormat={dateFormat}
                  onOpen={() => navigate(`/piece/${piece.id}`)}
                />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

interface QueueRowProps {
  piece: PieceRow;
  group: QueueGroup;
  dateFormat: ReturnType<typeof useDateFormat>;
  onOpen: () => void;
}

function QueueRow({ piece, group, dateFormat, onOpen }: QueueRowProps) {
  const reason = queueReasonFor(piece);
  const agent = stoppedByAgent(piece);
  const overdue = isOverdue(piece);

  return (
    <div
      className={`desk-row ${overdue ? "is-overdue" : ""}`}
      data-kind={group}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="desk-row-main">
        <div className="desk-row-top">
          <span className="desk-pill">
            <span className="desk-pill-dot" />
            {agent} · {reason.pill}
          </span>
          <span className="desk-row-reason">{reason.line}</span>
        </div>
        <span className="desk-row-title">{piece.title || piece.slug}</span>
        <span className="desk-row-meta">
          #{piece.id} · {formatContentType(piece.content_type)}
          {piece.target_word_count ? ` · ${piece.target_word_count.toLocaleString()} words` : ""}
        </span>
      </div>

      <div className="desk-row-side">
        <div className="desk-row-actions" onClick={(e) => e.stopPropagation()}>
          {/* Actions per group, per the handoff: Decide gets approve +
              send back, Read/Check the same minus approve, Fix a retry
              with no send-back, Start the pipeline-start arrow. All of
              them open the piece, because that is where each of those
              actions actually lives today — wiring them to act inline
              from the queue is its own story, not this one. */}
          <button
            type="button"
            className="desk-act"
            title="Open"
            aria-label="Open"
            onClick={onOpen}
          >
            <Icon name="chevron-r" size={15} stroke={1.6} />
          </button>
        </div>
        <span className="desk-row-when">
          {overdue && piece.publication_date ? (
            <span className="desk-overdue">
              Overdue · {formatDate(piece.publication_date, dateFormat)}
            </span>
          ) : (
            formatDateTime(piece.updated_at, dateFormat)
          )}
        </span>
      </div>
    </div>
  );
}

/** "Saturday, 29 August" — the eyebrow above the headline. Follows the
 * date-format setting like every other date on screen. */
function longToday(setting: ReturnType<typeof useDateFormat>): string {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(now);
  const rest = new Intl.DateTimeFormat(setting === "us" ? "en-US" : "en-GB", {
    day: "numeric",
    month: "long",
  }).format(now);
  return `${weekday}, ${rest}`;
}

function headline(waiting: number): string {
  if (waiting === 0) return "Nothing is waiting on you";
  return `${waiting} ${waiting === 1 ? "piece" : "pieces"} waiting on you`;
}

/**
 * The design's subtitle names the exceptions rather than restating the
 * count: what is late, what has not been started, and what is still with
 * the agents (explicitly so the reader knows it does NOT need them).
 */
function subtitle(overdue: number, ideas: number, running: number): string {
  const parts: string[] = [];
  if (overdue > 0) parts.push(`${overdue} ${overdue === 1 ? "is" : "are"} overdue`);
  if (ideas > 0) {
    parts.push(`${ideas} ${ideas === 1 ? "is an idea" : "are ideas"} you have not started`);
  }
  const head =
    parts.length === 0
      ? ""
      : `${parts.join(" and ").replace(/^./, (c) => c.toUpperCase())}. `;
  const tail =
    running > 0
      ? `${running} ${running === 1 ? "agent is" : "agents are"} still working — nothing there needs you.`
      : "No agents are running right now.";
  return `${head}${tail}`;
}
